/**
 * Rozbiór tekstu z pola „Imię i nazwisko" szybkiego dodawania osoby.
 *
 * Czysta logika bez Angulara, żeby dało się ją testować bez TestBed. Zasada
 * bazowa jest ta sama, co w wyszukiwarce (searchpersonfullname): ostatnie
 * słowo to nazwisko, reszta to imiona. Do tego kilka wygód:
 *   - „Kowalski, Jan" - nazwisko przed przecinkiem,
 *   - „Jan von Kowalski" - przedrostek nazwiska osobno,
 *   - „Jan Kowalski 1950", „… ur. 1950", „… *1950" - rok urodzenia na końcu,
 *   - samo „Anna" - tylko imię; nazwisko dopisze dziedziczenie po rodzicu.
 */

export interface ParsedName {
  /** Imiona (może być kilka), bez przedrostków. */
  names: string;
  surname: string;
  surnamePrefix: string;
  /** Czy nazwisko wpisał użytkownik - inaczej wolno je podstawić z dziedziczenia. */
  surnameTyped: boolean;
  /** Rok urodzenia wpisany na końcu, np. „1950". */
  birthYear?: string;
}

/** Cząstki nazwisk, które nie są ani imieniem, ani nazwiskiem. */
const PRZEDROSTKI = new Set([
  'von', 'van', 'der', 'den', 'de', 'di', 'da', 'del', 'della', 'dos', 'du', 'des',
  'le', 'la', 'zu', 'z', 'ze'
]);

/**
 * Męskie imiona na „-a" - wyjątki od reguły „końcówka -a = kobieta".
 * Lista celowo krótka: przełącznik płci i tak jest widoczny w oknie.
 */
const MESKIE_NA_A = new Set([
  'kuba', 'barnaba', 'bonawentura', 'kosma', 'jarema', 'sasza', 'ilja', 'nikita',
  'mustafa', 'juda', 'dyzma', 'joshua', 'ezra', 'luca', 'andrea'
]);

/** Popularne imiona żeńskie, które nie kończą się na „-a". */
const ZENSKIE_BEZ_A = new Set([
  'ruth', 'rut', 'miriam', 'ingrid', 'carmen', 'rachel', 'doris', 'nicole', 'michelle',
  'jennifer', 'agnes', 'ines', 'inez', 'margaret', 'elizabeth', 'mary', 'jane', 'anne',
  'ann', 'jean', 'sarah', 'hannah', 'judith', 'edith', 'esther', 'ester', 'noemi',
  'dagny', 'harriet', 'ellen', 'helen', 'catherine', 'irene', 'rose', 'alice', 'grace',
  'beatrice', 'charlotte', 'marie', 'sophie', 'julie', 'lucie', 'caroline', 'christine',
  'louise', 'susanne', 'elke', 'heike', 'gudrun', 'sigrid', 'astrid', 'inge', 'ute',
  'karin', 'kerstin', 'margit', 'birgit', 'liesl', 'gretel', 'nel', 'noor'
]);

/** Rok na końcu tekstu, opcjonalnie poprzedzony „ur.", „ur", „*" albo „b.". */
const ROK_NA_KONCU = /(?:^|\s)(?:ur\.?|b\.?|\*)?\s*(\d{4})\s*$/i;

export function parsePersonName(text: string): ParsedName {
  let rest = (text ?? '').replace(/\s+/g, ' ').trim();
  let birthYear: string | undefined;

  const rok = rest.match(ROK_NA_KONCU);
  if (rok) {
    birthYear = rok[1];
    rest = rest.slice(0, rok.index).trim();
  }

  const wynik: ParsedName = { names: '', surname: '', surnamePrefix: '', surnameTyped: false, birthYear };

  if (rest === '') {
    return wynik;
  }

  // „Kowalski, Jan" - nazwisko przed przecinkiem, imiona po nim
  const przecinek = rest.indexOf(',');
  if (przecinek >= 0) {
    const nazwiskoCzesc = rest.slice(0, przecinek).trim();
    wynik.names = rest.slice(przecinek + 1).trim();
    Object.assign(wynik, splitPrefix(nazwiskoCzesc.split(' ')));
    wynik.surnameTyped = wynik.surname !== '';
    return wynik;
  }

  const slowa = rest.split(' ');
  if (slowa.length === 1) {
    // samo imię - nazwisko przyjdzie z dziedziczenia
    wynik.names = slowa[0];
    return wynik;
  }

  // ostatnie słowo to nazwisko; bezpośrednio przed nim mogą stać przedrostki
  const nazwisko = slowa.pop() as string;
  const przedrostki: string[] = [];
  while (slowa.length > 0 && PRZEDROSTKI.has(slowa[slowa.length - 1].toLowerCase())) {
    przedrostki.unshift(slowa.pop() as string);
  }
  wynik.names = slowa.join(' ');
  wynik.surname = nazwisko;
  wynik.surnamePrefix = przedrostki.join(' ');
  wynik.surnameTyped = true;
  return wynik;
}

/** Część przed przecinkiem: „von Kowalski" → przedrostek + nazwisko. */
function splitPrefix(slowa: string[]): { surname: string; surnamePrefix: string } {
  const czesci = slowa.filter((slowo) => slowo !== '');
  if (czesci.length === 0) {
    return { surname: '', surnamePrefix: '' };
  }
  const nazwisko = czesci.pop() as string;
  return { surname: nazwisko, surnamePrefix: czesci.join(' ') };
}

/**
 * Płeć z pierwszego imienia - tylko podpowiedź do przełącznika w oknie.
 * Reguła polska: „-a" to kobieta (z wyjątkami), reszta to mężczyzna, chyba że
 * imię jest na krótkiej liście żeńskich imion obcych. Pusty tekst = nie wiadomo.
 */
export function guessSexFromName(names: string): 'M' | 'W' | '' {
  const pierwsze = (names ?? '').trim().split(' ')[0]?.toLowerCase() ?? '';
  if (pierwsze === '') {
    return '';
  }
  if (ZENSKIE_BEZ_A.has(pierwsze)) {
    return 'W';
  }
  if (MESKIE_NA_A.has(pierwsze)) {
    return 'M';
  }
  return pierwsze.endsWith('a') ? 'W' : 'M';
}

/**
 * Pierwsze imię w postaci do zapytania o płeć: pierwszy wyraz, bez kropki na
 * końcu; inicjał („J.") i zapisy „nie wiadomo" („NN") dają pusty tekst.
 * Odpowiada nameSexNormalize + nameSexKeyValid w backendzie.
 */
export function firstGivenName(names: string): string {
  const pierwsze = (names ?? '').trim().split(/\s+/)[0]?.replace(/\.+$/, '') ?? '';
  if (pierwsze.length < 2 || ['nn', 'n', 'x', 'xx', 'unknown', 'nieznany'].includes(pierwsze.toLowerCase())) {
    return '';
  }
  return /^\p{L}[\p{L}'’-]{1,39}$/u.test(pierwsze) ? pierwsze : '';
}

/**
 * Klucz pamięci podręcznej po stronie przeglądarki: małe litery i bez znaków
 * diakrytycznych - jak sf_given_name_key w bazie („Józef" = „Jozef").
 */
export function firstGivenNameKey(names: string): string {
  return firstGivenName(names)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ł/g, 'l');
}
